import { getSearch } from '@api/graphql/search';
import { getLocation } from '@core/geolocation';
import { getSafeUrl } from '@core/utils';
import { Layers, Loader, Package, Search, Store, Tag, X } from 'lucide-react';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';

type Kind = 'store' | 'product' | 'category' | 'brand';
type Option = { kind: Kind; key: string; title: string; subtitle?: string; href: string };

const GROUPS: { kind: Kind; label: string; icon: React.ReactNode }[] = [
    { kind: 'store', label: 'Stores', icon: <Store className="w-4 h-4" /> },
    { kind: 'product', label: 'Products', icon: <Package className="w-4 h-4" /> },
    { kind: 'category', label: 'Categories', icon: <Layers className="w-4 h-4" /> },
    { kind: 'brand', label: 'Brands', icon: <Tag className="w-4 h-4" /> },
];
const PER_GROUP = 4;

const storeHref = (s: any) => {
    const slug = getSafeUrl(s?.name);
    if (s?.sub_category_id === 29) return `/stays/${s.id}/${slug}`;   // hotels live under /stays
    if (s?.category_id === 1) return `/shop/${s.id}/${slug}`;          // grocery-style shops
    return `/store/${s.id}/${slug}`;
};

// One search over the marketplace (stores, products, categories, brands), in the same look as the
// hotel search next to it. Enter without a highlighted row runs a full product search.
export const ShopSearch: React.FC<{ className?: string }> = ({ className = '' }) => {
    const router = useRouter();
    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(-1);
    const [options, setOptions] = useState<Option[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);
    const runRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const listId = 'shop-search-options';

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('mousedown', onDown);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const run = (value: string) => {
        const q = value.trim();
        if (timerRef.current) clearTimeout(timerRef.current);
        setActive(-1);
        setFailed(false);
        if (q.length < 2) {
            runRef.current++;
            setOptions([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        timerRef.current = setTimeout(async () => {
            const id = ++runRef.current;
            try {
                const location = await getLocation();
                const res: any = await getSearch(q, 'all', { lat: location?.latitude, lon: location?.longitude });
                if (id !== runRef.current) return;
                const next: Option[] = [
                    ...(res?.get_stores || []).slice(0, PER_GROUP).map((s: any) => ({
                        kind: 'store' as Kind, key: `s${s.id}`, title: s.name, href: storeHref(s),
                        subtitle: s.sub_category_id === 29 ? 'Hotel' : undefined,
                    })),
                    ...(res?.get_unique_products || []).slice(0, PER_GROUP).map((p: any) => ({
                        kind: 'product' as Kind, key: `p${p.name}`, title: p.name,
                        subtitle: p.matches > 1 ? `${p.matches} listings` : undefined,
                        href: `/product/all?q=${encodeURIComponent(p.name)}&intent=product`,
                    })),
                    ...(res?.get_productcategories || []).slice(0, PER_GROUP).map((c: any) => ({
                        kind: 'category' as Kind, key: `c${c.id}`, title: c.name, href: `/category/${c.id}/${getSafeUrl(c.name)}`,
                    })),
                    ...(res?.get_brands || []).slice(0, PER_GROUP).map((b: any) => ({
                        kind: 'brand' as Kind, key: `b${b.id}`, title: b.name, href: `/brand/${b.id}/${getSafeUrl(b.name)}`,
                    })),
                ];
                setOptions(next);
            } catch {
                if (id === runRef.current) { setOptions([]); setFailed(true); }
            } finally {
                if (id === runRef.current) setLoading(false);
            }
        }, 300);
    };

    const go = (href: string) => { setOpen(false); router.push(href); };

    const submit = () => {
        if (active >= 0 && options[active]) return go(options[active].href);
        const q = term.trim();
        if (q) go(`/product/all?q=${encodeURIComponent(q)}&intent=product`);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault(); setOpen(true);
            setActive((i) => (options.length ? (i + 1) % options.length : -1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => (options.length ? (i <= 0 ? options.length - 1 : i - 1) : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault(); submit();
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    const showEmpty = term.trim().length >= 2 && !loading && !failed && options.length === 0;

    return (
        <div ref={wrapRef} className={`relative w-full max-w-2xl ${className}`}>
            <form role="search" onSubmit={(e) => { e.preventDefault(); submit(); }}
                className="flex items-center gap-2 bg-white rounded-2xl shadow-lg p-2 text-neutral-800">
                <Search className="w-5 h-5 ml-2 text-neutral-400 flex-shrink-0" aria-hidden="true" />
                <input
                    type="text" value={term} autoComplete="off"
                    onChange={(e) => { setTerm(e.target.value); setOpen(true); run(e.target.value); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder="Search stores, products or brands"
                    aria-label="Search stores, products, categories and brands"
                    role="combobox" aria-expanded={open && options.length > 0} aria-controls={listId} aria-autocomplete="list"
                    aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
                    className="flex-1 min-w-0 h-10 bg-transparent border-0 outline-none text-base text-neutral-800 placeholder:text-neutral-400"
                />
                {loading && <Loader className="w-4 h-4 animate-spin text-neutral-400 flex-shrink-0" />}
                {term && !loading && (
                    <button type="button" aria-label="Clear search" onClick={() => { setTerm(''); run(''); setOpen(false); }}
                        className="p-1.5 rounded-full text-neutral-400 hover:bg-neutral-100 border-0 bg-transparent cursor-pointer">
                        <X className="w-4 h-4" />
                    </button>
                )}
                <button type="submit" className="h-10 px-5 rounded-xl text-white text-sm font-semibold border-0 cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#ED7829' }}>
                    Search
                </button>
            </form>

            {open && (options.length > 0 || showEmpty || failed) && (
                <ul id={listId} role="listbox"
                    className="absolute left-0 right-0 mt-2 z-30 bg-white rounded-2xl shadow-xl border border-neutral-100 list-none p-2 m-0 max-h-96 overflow-auto text-left text-neutral-800">
                    {failed && <li className="px-3 py-3 text-sm text-neutral-500">Couldn&rsquo;t load suggestions. Press Search to look up &ldquo;{term}&rdquo;.</li>}
                    {showEmpty && <li className="px-3 py-3 text-sm text-neutral-500">No matches. Press Enter to search products for &ldquo;{term.trim()}&rdquo;.</li>}
                    {options.map((o, i) => {
                        const group = GROUPS.find((g) => g.kind === o.kind)!;
                        return (
                            <React.Fragment key={o.key}>
                                {(i === 0 || options[i - 1].kind !== o.kind) && (
                                    <li role="presentation" className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-wider text-neutral-400">{group.label}</li>
                                )}
                                <li id={`${listId}-${i}`} role="option" aria-selected={i === active}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseDown={(e) => { e.preventDefault(); go(o.href); }}
                                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer ${i === active ? 'bg-neutral-100' : ''}`}>
                                    <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-neutral-100 text-neutral-500 flex-shrink-0">{group.icon}</span>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold truncate">{o.title}</span>
                                        {o.subtitle && <span className="block text-xs text-neutral-400 truncate">{o.subtitle}</span>}
                                    </span>
                                </li>
                            </React.Fragment>
                        );
                    })}
                    {options.length > 0 && (
                        <li role="presentation" className="px-3 pt-2 pb-1 mt-1 border-t border-solid border-neutral-100">
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); submit(); }}
                                className="w-full text-left text-sm font-semibold text-business-500 bg-transparent border-0 cursor-pointer p-1 hover:underline">
                                See all results for &ldquo;{term.trim()}&rdquo;
                            </button>
                        </li>
                    )}
                </ul>
            )}
        </div>
    );
};
